import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, X, ChevronLeft, ChevronRight, Share2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import CompanyTable from './publicCompeniesTable';
import {publicCompanies,changeCompanyStatus} from '../../services/companiesapi'
// import JobDetailSidebar from './ViewAllJobs/JobDetailSidebar';
// import JobReassignmentModal from '../../components/ViewAllJobs/JobReassignmentModal';
// import { getAllJobs, updateJobStatus } from '../../services/jobapi';
import { toast } from 'react-hot-toast'; 
import { Label } from '../ui/label';
interface Job {
  id: string;
  companyName: string;
  hrName: string;
  hrEmail: string;
  hrContact: string;
  isApproved: boolean;
  isjobDetailsAdded: boolean;
  jobTitle: string;
  jobCategory: string;
  shiftTimings: string;
  employmentType: string;
  workType: string;
  jobDescription: string;
  streetAddress: string;
  area: string;
  city: string;
  pincode: string;
  locationLink: string;
  minSalary: number;
  maxSalary: number;
  skills: string[];
  spokenLanguages: string[];
  noOfOpenings: number;
  hiringNeed: string;
  minQualification: string;
  totalExperience: number;
  createdAt: string;
}
const PublicCompaniesView = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    status: ''
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [jobAdded, setJobAdded] = useState(false);
  const [jobEdited, setJobEdited] = useState(false);
  const [statusLoading, setStatusLoading] = useState<string | null>(null);


  const activeFilters = Object.entries(filters).filter(([_, value]) => value !== '');

  // Handler for sorting
  const onSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Handler for page change to pass to JobTable
  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const removeFilter = (key: string) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
    setCurrentPage(1); // Reset to first page when removing filter
  };

  const clearAllFilters = () => {
    setFilters({ category: '', status: '' });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Update job status handler
  const handleStatusToggle = async (job: Job) => {
    if (!job) return;
    
    setStatusLoading(job.id);
    const newApproved = !job.isApproved;
    
    try {
      // Call API to update job status
      const response = await changeCompanyStatus(job.id, newApproved);
      
      // Only update state if API call was successful
      if (response && response.message) {
        toast.success(`Job ${newApproved ? 'approved' : 'disapproved'} successfully`);
        
        // Update jobs list locally - ONLY on success
        setJobs(prevJobs => 
          prevJobs.map(j => 
            j.id === job.id ? { ...j, isApproved: newApproved } : j
          )
        );

        // Update selected job if it's the same job - ONLY on success
        if (selectedJob && selectedJob.id === job.id) {
          setSelectedJob({ ...selectedJob, isApproved: newApproved });
        }
      } else {
        // If response doesn't have expected structure, treat as error
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Failed to update job approval:', error);
      // Don't update state on error - keep original isApproved value
      // Extract the actual error message from various possible locations
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to update job approval';
      toast.error(errorMessage);
    } finally {
      setStatusLoading(null);
    }
  };

  // Fetch jobs from backend with query parameters
  const fetchJobs = async () => {
    setIsLoading(true);
    console.log('Fetching jobs...', filters.status);

    try {
      const queryParams = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        category: filters.category,
        status: filters.status,
      };
      console.log("queryParams", queryParams);

      const response = await publicCompanies(queryParams);
      const mappedJobs = response.jobs.map((job: any) => ({
        ...job,
        id: job._id
      }));
      setJobs(mappedJobs);
      if (selectedJob) {
        const updatedSelectedJob = mappedJobs.find((job: Job) => job.id === selectedJob.id);
        if (updatedSelectedJob) {
          setSelectedJob(updatedSelectedJob);
        } else {
          setSelectedJob(null); // Job might have been deleted
        }
      }
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (jobAdded || jobEdited) {
      fetchJobs();
      setJobAdded(false);
      setJobEdited(false);
    }
  }, [jobAdded, jobEdited]);

  useEffect(() => {
    fetchJobs();
  }, [currentPage, itemsPerPage, searchTerm, filters.category, filters.status]);



  const handleReassignSuccess = () => {
    fetchJobs();
  };

  // Handle job updates from sidebar
  const handleJobUpdate = (updatedJob: Job) => {
    setJobs(prevJobs => 
      prevJobs.map(j => 
        j.id === updatedJob.id ? updatedJob : j
      )
    );
    setSelectedJob(updatedJob);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 6;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1 || isLoading}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {pageNumbers.map(page => (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCurrentPage(page)}
            disabled={isLoading}
            className={currentPage === page ? 'bg-[#EC6E51] text-primary-foreground' : ''}
          >
            {page}
          </Button>
        ))}

        {endPage < totalPages && (
          <>
            <span className="text-sm text-muted-foreground">...</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={isLoading}
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages || isLoading}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4 bg-background">
      <div className="sticky top-0 z-40 bg-background border-b border-border">
        <div className="px-6 py-4">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search by title, company, category, or location"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10"
                  style={{ maxWidth: "450px" }}
                />
              </div>
              <Select
                value={filters.category || 'all'}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, category: value === 'all' ? '' : value }));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Aviation">Aviation</SelectItem>
                  <SelectItem value="Banking">Banking</SelectItem>
                  <SelectItem value="Insurance">Insurance</SelectItem>
                  <SelectItem value="Oil And Gas">Oil And Gas</SelectItem>
                  <SelectItem value="Retail">Retail</SelectItem>
                  <SelectItem value="Education">Education</SelectItem>
                  <SelectItem value="Consumer Goods">Consumer Goods</SelectItem>
                  <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                  <SelectItem value="Information Technology">Information Technology</SelectItem>
                  <SelectItem value="Health Care">Health Care</SelectItem>
                  <SelectItem value="BPO">BPO</SelectItem>
                  <SelectItem value="ITES">ITES</SelectItem>
                  <SelectItem value="Entertainment">Entertainment</SelectItem>
                  <SelectItem value="Finance">Finance</SelectItem>
                  <SelectItem value="Textile">Textile</SelectItem>
                  <SelectItem value="Media and news">Media and news</SelectItem>
                  <SelectItem value="Food processing">Food processing</SelectItem>
                  <SelectItem value="Hospitality">Hospitality</SelectItem>
                  <SelectItem value="Construction">Construction</SelectItem>
                  <SelectItem value="Law">Law</SelectItem>
                  <SelectItem value="Advertising">Advertising</SelectItem>
                  <SelectItem value="E-commerce">E-commerce</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, status: value === 'all' ? '' : value }));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              {/* <Button
              onClick={() => {
                const url = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/freejobposting`;
                navigator.clipboard.writeText(url);
                toast.success('Company Onboard Form link copied to clipboard');
              }}
              className="bg-orange-600 hover:bg-[#a53609] text-white flex items-center gap-2"
            >
              <Share2 size={18} />
              Share Company Onboard Form
            </Button> */}

                </div>

            {(activeFilters.length > 0 || searchTerm) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {searchTerm && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Search: {searchTerm}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                      }}
                    />
                  </Badge>
                )}
                {activeFilters.map(([key, value]) => (
                  <Badge key={key} variant="secondary" className="flex items-center gap-1">
                    {key}: {value}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => removeFilter(key)}
                    />
                  </Badge>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-xs"
                >
                  Clear all
                </Button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 justify-end" style={{ width: '100%' }}>
                {/* Add any additional controls if needed */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-275px)] overflow-y-scroll">
        <div className={`flex-1 transition-all duration-300`}>
          <CompanyTable
            jobs={jobs}
            setJobs={setJobs}
            isloading={isLoading}
            onJobSelect={setSelectedJob}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onStatusToggle={handleStatusToggle}
            statusLoading={statusLoading}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end p-[20px]">
        {renderPagination()}
      </div>

      {/* Job Details Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-4 border-b mx-w-[3rem]" style={{maxWidth:"80%"}}> 
            <DialogTitle className="text-2xl font-bold">{selectedJob?.jobTitle}</DialogTitle>
            <DialogClose className="absolute right-4 top-4" />
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-6 py-6">
              {/* Company Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Company Details</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Company Name</Label>
                    <p className="font-medium">{selectedJob.companyName}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Approval Status</Label>
                    <Badge variant={selectedJob.isApproved ? "success" : "destructive"}>
                      {selectedJob.isApproved ? "Approved" : "Pending"}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* HR Details */}
              <Card>
                <CardHeader>
                  <CardTitle>HR Contact</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">HR Name</Label>
                    <p className="font-medium">{selectedJob.hrName}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">HR Email</Label>
                    <p className="font-medium">{selectedJob.hrEmail}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">HR Contact</Label>
                    <p className="font-medium">{selectedJob.hrContact}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Job Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Job Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Category</Label>
                      <Badge>{selectedJob.jobCategory}</Badge>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Shift Timings</Label>
                      <p className="font-medium">{selectedJob.shiftTimings}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Employment Type</Label>
                      <p className="font-medium">{selectedJob.employmentType}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Work Type</Label>
                      <p className="font-medium capitalize">{selectedJob.workType}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Description</Label>
                    <div
                      className="prose prose-sm max-w-none text-gray-700 leading-relaxed
                        [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-1
                        [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:space-y-1
                        [&_li]:mb-1
                        [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-4
                        [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-3
                        [&_h3]:text-base [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-2
                        [&_p]:mb-3
                        [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600
                        [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono
                        [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto
                        [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800
                        [&_strong]:font-bold
                        [&_em]:italic
                        [&_u]:underline
                        [&_s]:line-through"
                      dangerouslySetInnerHTML={{ __html: selectedJob.jobDescription }}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Location Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Location</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Street Address</Label>
                    <p className="font-medium">{selectedJob.streetAddress}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Area</Label>
                    <p className="font-medium">{selectedJob.area}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">City</Label>
                    <p className="font-medium">{selectedJob.city}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Pincode</Label>
                    <p className="font-medium">{selectedJob.pincode}</p>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-muted-foreground">Location Link</Label>
                    <a href={selectedJob.locationLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline block">
                      {selectedJob.locationLink}
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Salary and Openings */}
              <Card>
                <CardHeader>
                  <CardTitle>Salary and Openings</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Min Salary</Label>
                    <p className="font-medium">₹{selectedJob.minSalary.toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Max Salary</Label>
                    <p className="font-medium">₹{selectedJob.maxSalary.toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">No. of Openings</Label>
                    <p className="font-medium">{selectedJob.noOfOpenings}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Requirements */}
              <Card>
                <CardHeader>
                  <CardTitle>Requirements</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-muted-foreground">Skills</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedJob.skills.map((skill, index) => (
                        <Badge key={index} variant="secondary">{skill}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Spoken Languages</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedJob.spokenLanguages.map((lang, index) => (
                        <Badge key={index} variant="secondary">{lang}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Hiring Need</Label>
                      <p className="font-medium">{selectedJob.hiringNeed}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Min Qualification</Label>
                      <p className="font-medium">{selectedJob.minQualification}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Total Experience</Label>
                      <p className="font-medium">{selectedJob.totalExperience} years</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Metadata */}
              <div className="text-sm text-muted-foreground">
                Created on: {new Date(selectedJob.createdAt).toLocaleString()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default PublicCompaniesView;